import {
    CreateNewFolderOutlined,
    DeleteOutline,
    DriveFileMoveOutlined,
    MoreHoriz,
} from '@mui/icons-material';
import {
    Box,
    IconButton,
    List,
    ListItem,
    Menu,
    Typography,
} from '@mui/material';
import { MouseEvent, memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { catalogItemActions } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import { getLocalCategoriesList } from 'entities/Category/model/selectors/categorySelectors';
import { getUserCanManageCatalogItems } from 'entities/User/model/selectors/userPermissionsSeletors';
import { getAddCatalogItemMode } from 'features/AddCatalogItem/model/selectors/addCatalogItemSelectors';
import {
    ChangeCatalogItemCategoryDialog,
    deriveChangeCatalogItemCategoryState,
} from 'features/changeCatalogItemCategory';
import { moveToInventoryItemActions } from 'features/moveToInventoryCatalogItem/model/slices/MoveToInventoryItemSlice';
import { removeCatalogItemActions } from 'features/removeCatalogItem/model/slices/RemoveCatalogItemSlice';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { FindReplacementLink } from 'shared/ui/FindReplacementLink/FindReplacementLink';
import { ShelfPositionIcon } from 'shared/ui/icons';
import { PropertyCatalogItemsMoveTo } from 'widgets/PropertyCatalog/models/consts/propertyCatalogItemsListTypes';

import type { CatalogCategoryChangeFollowUp } from 'features/changeCatalogItemCategory';

interface PropertyCatalogItemRowActionsProps {
    selectionKey: string;
    itemUid: string;
    catalogItemId?: string;
    propertyCategoryId?: string;
    title: string;
    moveTo: PropertyCatalogItemsMoveTo;
    showFindReplacement?: boolean;
    onAfterCatalogCategoryChange?: CatalogCategoryChangeFollowUp;
}

export const PropertyCatalogItemRowActions = memo(
    ({
        selectionKey,
        itemUid,
        catalogItemId,
        propertyCategoryId,
        title,
        moveTo,
        showFindReplacement = false,
        onAfterCatalogCategoryChange,
    }: PropertyCatalogItemRowActionsProps) => {
        const { t } = useTranslation();
        const dispatch = useAppDispatch();
        const location = useLocation();
        const canEditCatalog = useSelector(getUserCanManageCatalogItems);
        const isEditingCatalog = useSelector(getAddCatalogItemMode);
        const localCategories = useSelector(getLocalCategoriesList);
        const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
        const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);

        const isCatalogSection =
            location.pathname.startsWith('/catalog') &&
            moveTo === PropertyCatalogItemsMoveTo.CATALOG &&
            !isEditingCatalog &&
            canEditCatalog &&
            Boolean(catalogItemId);

        const selectOnlyThisItem = useCallback(() => {
            dispatch(catalogItemActions.removeAllSelection());
            dispatch(
                catalogItemActions.addMultiselectItem({
                    id: selectionKey,
                    uid: itemUid,
                    catalogItemId,
                    title,
                    propertyCategoryId,
                }),
            );
        }, [
            catalogItemId,
            dispatch,
            itemUid,
            propertyCategoryId,
            selectionKey,
            title,
        ]);

        const onRemove = useCallback(
            (event?: MouseEvent) => {
                event?.stopPropagation();
                setMenuAnchor(null);
                // Clear detailed selection so dialog payload uses multiselect,
                // not a stale selectedItem from another row.
                dispatch(catalogItemActions.closeDetailedItem());
                selectOnlyThisItem();
                dispatch(removeCatalogItemActions.setIsSingle(true));
                dispatch(removeCatalogItemActions.setShowDialog(true));
            },
            [dispatch, selectOnlyThisItem],
        );

        const onAddToInventory = useCallback(
            (event?: MouseEvent) => {
                event?.stopPropagation();
                setMenuAnchor(null);
                // Clear detailed selection so dialog payload uses multiselect,
                // not a stale selectedItem from another row.
                dispatch(catalogItemActions.closeDetailedItem());
                selectOnlyThisItem();
                dispatch(moveToInventoryItemActions.setIsSingle(true));
                dispatch(moveToInventoryItemActions.setShowDialog(true));
            },
            [dispatch, selectOnlyThisItem],
        );

        const onEditCategory = useCallback((event?: MouseEvent) => {
            event?.stopPropagation();
            setMenuAnchor(null);
            setCategoryDialogOpen(true);
        }, []);

        const categoryChangeState = useMemo(() => {
            if (!catalogItemId || propertyCategoryId === undefined) {
                return null;
            }

            return deriveChangeCatalogItemCategoryState(
                [
                    {
                        id: catalogItemId,
                        title,
                        categoryId: propertyCategoryId,
                    },
                ],
                localCategories,
                t,
            );
        }, [catalogItemId, localCategories, propertyCategoryId, t, title]);

        if (!isCatalogSection) {
            return null;
        }

        const isMenuOpen = Boolean(menuAnchor);
        const menuItemSx = {
            padding: '10px 12px',
            gap: '12px',
            cursor: 'pointer',
            ':hover': { bgcolor: 'action.hover' },
        };
        const menuIconSx = {
            width: '20px',
            height: '20px',
            color: 'rgba(0, 0, 0, 0.54)',
        };
        const menuLabelSx = {
            fontSize: '14px',
            fontFamily: 'Open Sans',
            fontWeight: 400,
            lineHeight: '20px',
            color: 'rgba(0, 0, 0, 0.87)',
        };
        const CategoryMenuIcon = categoryChangeState?.useAddCategoryTitle
            ? CreateNewFolderOutlined
            : DriveFileMoveOutlined;

        return (
            <Box
                className="catalog-item-row-actions"
                onClick={(event) => event.stopPropagation()}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                }}
            >
                {showFindReplacement && (
                    <Box
                        className="catalog-item-row-hover-action"
                        sx={{
                            display: 'flex',
                            marginRight: '4px',
                            opacity: isMenuOpen ? 1 : 0,
                            transition: 'opacity 0.15s ease',
                        }}
                    >
                        <FindReplacementLink />
                    </Box>
                )}
                <IconButton
                    className="catalog-item-row-hover-action"
                    data-testclass="catalog-item-remove"
                    aria-label={t('Remove')}
                    size="small"
                    onClick={onRemove}
                    sx={{
                        padding: '4px',
                        opacity: isMenuOpen ? 1 : 0,
                        color: 'rgba(0, 0, 0, 0.54)',
                        transition: 'opacity 0.15s ease',
                        '&:hover': {
                            bgcolor: 'transparent',
                            color: 'rgba(0, 0, 0, 0.87)',
                        },
                    }}
                >
                    <DeleteOutline sx={{ width: '20px', height: '20px' }} />
                </IconButton>
                <IconButton
                    data-testclass="catalog-item-more"
                    aria-label="More actions"
                    size="small"
                    onClick={(event) => {
                        event.stopPropagation();
                        setMenuAnchor(event.currentTarget);
                    }}
                    sx={{
                        padding: '4px',
                        color: 'rgba(0, 0, 0, 0.54)',
                        '&:hover': {
                            bgcolor: 'transparent',
                            color: 'rgba(0, 0, 0, 0.87)',
                        },
                    }}
                >
                    <MoreHoriz sx={{ width: '20px', height: '20px' }} />
                </IconButton>
                <Menu
                    anchorEl={menuAnchor}
                    open={isMenuOpen}
                    onClose={() => setMenuAnchor(null)}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    slotProps={{
                        paper: {
                            elevation: 0,
                            sx: {
                                overflow: 'visible',
                                filter: 'drop-shadow(0px 2px 12px rgba(0, 0, 0, 0.1))',
                                minWidth: '200px',
                                borderRadius: '6px',
                                marginTop: '8px',
                            },
                        },
                    }}
                >
                    <List disablePadding>
                        {categoryChangeState ? (
                            <ListItem onClick={onEditCategory} sx={menuItemSx}>
                                <CategoryMenuIcon sx={menuIconSx} />
                                <Typography sx={menuLabelSx}>
                                    {categoryChangeState.useAddCategoryTitle
                                        ? t('Add Category')
                                        : t('Edit Category')}
                                </Typography>
                            </ListItem>
                        ) : null}
                        <ListItem onClick={onAddToInventory} sx={menuItemSx}>
                            <ShelfPositionIcon sx={menuIconSx} />
                            <Typography sx={menuLabelSx}>
                                {t('Add to Inventory')}
                            </Typography>
                        </ListItem>
                    </List>
                </Menu>
                {categoryDialogOpen && categoryChangeState ? (
                    <ChangeCatalogItemCategoryDialog
                        changeCatalogState={categoryChangeState}
                        onAfterCatalogCategoryChange={
                            onAfterCatalogCategoryChange
                        }
                        onClose={() => setCategoryDialogOpen(false)}
                    />
                ) : null}
            </Box>
        );
    },
);
